import { useEffect, useState } from 'react'
import type { Lead } from '@/types/crm'

/**
 * Step 0, at the top of an unverified lead.
 *
 * Every lead starts here, so this is the highest-traffic surface in the CRM and
 * the one that has to be fastest. It carries only the three fields that decide
 * whether a lead is workable at all — the website above everything, because
 * without a company domain the Cloud Functions cannot attribute a Skida PRO
 * signup back to this lead and every later automation is blind.
 *
 * Everything else waits. A rep verifying 200 leads is answering one question
 * per lead — is this real and worth working — not filling in a form.
 */
export function VerifyPanel({
  lead,
  onSave,
  onVerify,
  onArchive,
}: {
  lead: Lead
  onSave: (patch: { website: string; country: string; region: string }) => Promise<void>
  onVerify: () => Promise<void>
  onArchive: () => Promise<void>
}) {
  const [website, setWebsite] = useState(lead.website)
  const [country, setCountry] = useState(lead.country)
  const [region, setRegion] = useState(lead.region)
  const [busy, setBusy] = useState<'save' | 'verify' | 'archive' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Switching leads inside the drawer must not carry the previous one's edits.
  useEffect(() => {
    setWebsite(lead.website)
    setCountry(lead.country)
    setRegion(lead.region)
    setError(null)
  }, [lead.id, lead.website, lead.country, lead.region])

  const dirty =
    website !== lead.website || country !== lead.country || region !== lead.region

  const run = async (what: 'save' | 'verify' | 'archive', fn: () => Promise<void>) => {
    if (busy) return
    setBusy(what)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="verify">
      <div className="verify__head">
        <div className="card-label verify__label">Step 0 — verify</div>
        <div className="micro">
          Fix what is wrong, then accept or archive. Archived leads are kept, not deleted.
        </div>
      </div>

      {error && <div className="modal__error">{error}</div>}

      <div className="verify__fields">
        <label className="verify__field">
          <span>Website</span>
          <input
            className="input"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="company.no"
            autoFocus
          />
        </label>
        <label className="verify__field">
          <span>Country</span>
          <input
            className="input"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="Norway"
          />
        </label>
        <label className="verify__field">
          <span>Region</span>
          <input
            className="input"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Lofoten"
          />
        </label>
      </div>

      {!website.trim() && (
        <div className="micro verify__warn">
          No website. Without a company domain nothing can link a Skida PRO signup back to this
          lead — find one, or archive it.
        </div>
      )}

      <div className="verify__actions">
        <button
          className="btn btn--ghost"
          disabled={!dirty || busy !== null}
          onClick={() =>
            void run('save', () => onSave({ website, country, region }))
          }
        >
          {busy === 'save' ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
        <div className="verify__spacer" />
        <button
          className="btn btn--archive"
          disabled={busy !== null}
          onClick={() => void run('archive', onArchive)}
        >
          {busy === 'archive' ? 'Archiving…' : 'Archive'}
        </button>
        <button
          className="btn btn--primary"
          disabled={busy !== null}
          onClick={() =>
            void run('verify', async () => {
              // Save first, so a corrected website is not lost by the move.
              if (dirty) await onSave({ website, country, region })
              await onVerify()
            })
          }
        >
          {busy === 'verify' ? 'Verifying…' : 'Verify → Prospect'}
        </button>
      </div>
    </section>
  )
}
